import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Patient {
  id: string
  researchId: string
  patientId: string
  firstName: string
  lastName: string
  name: string
  gender: string
  age: number
  birthDate: string
  diseaseName: string
  diseaseDetail: string
  treatment: string
  primaryHospitalId: string
  primaryHospitalName: string
  transplantHospitalId: string
  transplantHospitalName: string
  primaryDoctorId: string
  primaryDoctorName: string
  transplantDoctorId: string
  transplantDoctorName: string
  transplantCount: number
  evaluation: string
  adverseEvents: string | null
  createdAt: string
  updatedAt: string
}

interface PatientState {
  patients: Patient[]
  addPatient: (patient: Omit<Patient, 'id' | 'researchId' | 'patientId' | 'createdAt' | 'updatedAt'>) => string
  getPatient: (id: string) => Patient | undefined
  getPatientsByHospital: (hospitalId: string) => Patient[]
  updatePatient: (id: string, data: Partial<Patient>) => void
  deletePatient: (id: string) => void
}

// 初期サンプルデータ
const initialPatients: Patient[] = [
  {
    id: 'pat-001',
    researchId: 'R01166',
    patientId: '39-011',
    firstName: 'Kn',
    lastName: 'Ku',
    name: 'KuKn',
    gender: '女性',
    age: 33,
    birthDate: '1990-05-15',
    diseaseName: '潰瘍性大腸炎',
    diseaseDetail: '潰瘍性大腸炎（UC）',
    treatment: 'FMT3回コース',
    primaryHospitalId: 'hosp-001',
    primaryHospitalName: 'Baseクリニック赤坂',
    transplantHospitalId: 'hosp-001',
    transplantHospitalName: 'Baseクリニック赤坂',
    primaryDoctorId: 'doc-001',
    primaryDoctorName: '吉岡 紘平',
    transplantDoctorId: 'doc-001',
    transplantDoctorName: '吉岡 紘平',
    transplantCount: 3,
    evaluation: '症状の軽度改善',
    adverseEvents: null,
    createdAt: '2025-05-01',
    updatedAt: '2025-11-28',
  },
  {
    id: 'pat-002',
    researchId: 'R01598',
    patientId: '13-079',
    firstName: 'Hrs',
    lastName: 'Itu',
    name: 'ItuHrs',
    gender: '男性',
    age: 38,
    birthDate: '1985-08-20',
    diseaseName: '潰瘍性大腸炎',
    diseaseDetail: '潰瘍性大腸炎',
    treatment: '継続治療中',
    primaryHospitalId: 'hosp-004',
    primaryHospitalName: '二子玉川メディカルクリニック',
    transplantHospitalId: 'hosp-004',
    transplantHospitalName: '二子玉川メディカルクリニック',
    primaryDoctorId: 'doc-004',
    primaryDoctorName: '佐藤 健一',
    transplantDoctorId: 'doc-004',
    transplantDoctorName: '佐藤 健一',
    transplantCount: 6,
    evaluation: '症状の中程度の改善',
    adverseEvents: null,
    createdAt: '2024-11-01',
    updatedAt: '2025-11-27',
  },
  {
    id: 'pat-006',
    researchId: 'R00483',
    patientId: '25-012',
    firstName: 'Chr',
    lastName: 'Aihr',
    name: 'AihrChr',
    gender: '女性',
    age: 51,
    birthDate: '1972-07-18',
    diseaseName: '過敏性腸症候群',
    diseaseDetail: '過敏性腸症候群',
    treatment: 'FMT',
    primaryHospitalId: 'hosp-003',
    primaryHospitalName: '城谷バイオウェルネスクリニック',
    transplantHospitalId: 'hosp-003',
    transplantHospitalName: '城谷バイオウェルネスクリニック',
    primaryDoctorId: 'doc-003',
    primaryDoctorName: '城谷 昌彦',
    transplantDoctorId: 'doc-003',
    transplantDoctorName: '城谷 昌彦',
    transplantCount: 2,
    evaluation: '治癒',
    adverseEvents: null,
    createdAt: '2025-08-01',
    updatedAt: '2025-11-20',
  },
  {
    id: 'pat-007',
    researchId: 'R01700',
    patientId: '48-001',
    firstName: 'Mki',
    lastName: 'Kmr',
    name: 'KmrMki',
    gender: '女性',
    age: 35,
    birthDate: '1988-03-22',
    diseaseName: 'アトピー性皮膚炎',
    diseaseDetail: 'アトピー性皮膚炎',
    treatment: 'FMT',
    primaryHospitalId: 'hosp-002',
    primaryHospitalName: 'サンテクリニック',
    transplantHospitalId: 'hosp-002',
    transplantHospitalName: 'サンテクリニック',
    primaryDoctorId: 'doc-002',
    primaryDoctorName: '山田 太郎',
    transplantDoctorId: 'doc-002',
    transplantDoctorName: '山田 太郎',
    transplantCount: 3,
    evaluation: '著効',
    adverseEvents: null,
    createdAt: '2025-09-01',
    updatedAt: '2025-11-25',
  },
  {
    id: 'pat-008',
    researchId: 'R01701',
    patientId: '48-002',
    firstName: 'Tky',
    lastName: 'Ymd',
    name: 'YmdTky',
    gender: '男性',
    age: 48,
    birthDate: '1975-11-10',
    diseaseName: 'うつ病',
    diseaseDetail: 'うつ病',
    treatment: 'FMT',
    primaryHospitalId: 'hosp-001',
    primaryHospitalName: 'Baseクリニック赤坂',
    transplantHospitalId: 'hosp-001',
    transplantHospitalName: 'Baseクリニック赤坂',
    primaryDoctorId: 'doc-001',
    primaryDoctorName: '吉岡 紘平',
    transplantDoctorId: 'doc-001',
    transplantDoctorName: '吉岡 紘平',
    transplantCount: 4,
    evaluation: '軽度改善',
    adverseEvents: null,
    createdAt: '2025-10-01',
    updatedAt: '2025-11-24',
  },
]

// 研究会IDを生成
const generateResearchId = () => {
  const num = Math.floor(Math.random() * 90000) + 10000
  return `R${num}`
}

// 患者IDを生成
const generatePatientId = () => {
  const prefix = Math.floor(Math.random() * 90) + 10
  const suffix = Math.floor(Math.random() * 900) + 100
  return `${prefix}-${suffix}`
}

export const usePatientStore = create<PatientState>()(
  persist(
    (set, get) => ({
      patients: initialPatients,

      addPatient: (patientData) => {
        const id = `pat-${Date.now()}`
        const now = new Date().toISOString().split('T')[0]
        const newPatient: Patient = {
          ...patientData,
          id,
          researchId: generateResearchId(),
          patientId: generatePatientId(),
          createdAt: now,
          updatedAt: now,
        }

        set((state) => ({
          patients: [newPatient, ...state.patients],
        }))

        return id
      },

      getPatient: (id: string) => {
        return get().patients.find((p) => p.id === id)
      },

      getPatientsByHospital: (hospitalId: string) => {
        return get().patients.filter(
          (p) => p.primaryHospitalId === hospitalId || p.transplantHospitalId === hospitalId
        )
      },

      updatePatient: (id: string, data: Partial<Patient>) => {
        set((state) => ({
          patients: state.patients.map((patient) =>
            patient.id === id
              ? { ...patient, ...data, updatedAt: new Date().toISOString().split('T')[0] }
              : patient
          ),
        }))
      },

      deletePatient: (id: string) => {
        set((state) => ({
          patients: state.patients.filter((patient) => patient.id !== id),
        }))
      },
    }),
    {
      name: 'fmt-japan-patients',
    }
  )
)

